/**
 * Dictionary Generation Hook
 *
 * Provides a function to generate the output variable/meter dictionary
 * by running a quick sizing-only simulation on the current model.
 */

import { useCallback } from 'react';
import { useSimulationStore } from '../simulation/simulationStore';
import {
  useOutputLookupStore,
  selectVariableCount,
  selectMeterCount,
} from './outputLookupStore';
import {
  prepareIDFForDictionaryGeneration,
  canGenerateDictionary,
} from '../simulation/dictionaryGenerator';

export interface DictionaryGenerationResult {
  success: boolean;
  variableCount: number;
  meterCount: number;
  error?: string;
}

/**
 * Hook to generate the output variable dictionary
 *
 * Returns a function that takes the current IDF content and runs
 * a sizing-only simulation to generate the RDD/MDD files.
 */
export function useDictionaryGeneration() {
  const setDictionaryStatus = useOutputLookupStore((state) => state.setDictionaryStatus);
  const dictionaryStatus = useOutputLookupStore((state) => state.dictionaryStatus);
  // Use selectors that compute from actual data
  const variableCount = useOutputLookupStore(selectVariableCount);
  const meterCount = useOutputLookupStore(selectMeterCount);
  const dictionaryError = useOutputLookupStore((state) => state.dictionaryError);

  const loadWasm = useSimulationStore((state) => state.loadWasm);
  const setInput = useSimulationStore((state) => state.setInput);
  const startSimulation = useSimulationStore((state) => state.startSimulation);
  const wasmLoaded = useSimulationStore((state) => state.wasmLoaded);
  const simulationStatus = useSimulationStore((state) => state.status);

  const generateDictionary = useCallback(
    async (idfContent: string): Promise<DictionaryGenerationResult> => {
      // Validate the IDF
      const validation = canGenerateDictionary(idfContent);
      if (!validation.canGenerate) {
        const error = `Cannot generate dictionary: missing ${validation.missingElements.join(', ')}`;
        setDictionaryStatus('error', error);
        return { success: false, variableCount: 0, meterCount: 0, error };
      }

      try {
        setDictionaryStatus('generating');

        // Load WASM if not loaded
        if (!wasmLoaded) {
          await loadWasm();
        }

        // Prepare the IDF for dictionary generation
        const preparedIDF = prepareIDFForDictionaryGeneration(idfContent);

        // Set the input
        setInput({
          idf: preparedIDF,
          idfName: 'dictionary-generation.idf',
        });

        // Start the simulation
        await startSimulation();

        // Wait for completion by polling the simulation status
        // The RDD/MDD loading happens automatically in simulationStore.setComplete
        return new Promise((resolve) => {
          const checkStatus = () => {
            const currentStatus = useSimulationStore.getState().status;
            const lookupState = useOutputLookupStore.getState();

            if (currentStatus === 'complete') {
              resolve({
                success: true,
                variableCount: selectVariableCount(lookupState),
                meterCount: selectMeterCount(lookupState),
              });
            } else if (currentStatus === 'error') {
              const error = useSimulationStore.getState().error ?? 'Unknown error';
              setDictionaryStatus('error', error);
              resolve({
                success: false,
                variableCount: 0,
                meterCount: 0,
                error,
              });
            } else if (currentStatus === 'cancelled') {
              setDictionaryStatus('error', 'Generation was cancelled');
              resolve({
                success: false,
                variableCount: 0,
                meterCount: 0,
                error: 'Generation was cancelled',
              });
            } else {
              // Still running, check again in 100ms
              setTimeout(checkStatus, 100);
            }
          };

          checkStatus();
        });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        setDictionaryStatus('error', errorMessage);
        return {
          success: false,
          variableCount: 0,
          meterCount: 0,
          error: errorMessage,
        };
      }
    },
    [loadWasm, setInput, startSimulation, wasmLoaded, setDictionaryStatus]
  );

  return {
    generateDictionary,
    isGenerating: dictionaryStatus === 'generating' || simulationStatus === 'running',
    isComplete: dictionaryStatus === 'complete',
    error: dictionaryError,
    variableCount,
    meterCount,
  };
}
